import { Link, createFileRoute } from "@tanstack/react-router";
import { XIcon } from "lucide-react";

import { DebouncedInput } from "~/components/debounced-input";
import { Field } from "~/components/filter-controls";
import { PageHeader } from "~/components/page-header";
import { PaginationBar } from "~/components/pagination-bar";
import { TableEmpty, TableError, TableSkeleton } from "~/components/table-states";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { errorMessage } from "~/lib/api";
import { formatDateTime, formatIDR, orDash } from "~/lib/format";
import { useDisbursements } from "~/lib/queries";
import {
  hasActiveFilters,
  toFilters,
  validateDisbursementSearch,
  type DisbursementSearch,
} from "./-search";

export const Route = createFileRoute("/_authed/disbursements/")({
  validateSearch: validateDisbursementSearch,
  component: DisbursementsPage,
});

const COLUMNS = 4;

/**
 * Payouts out of Kugie's account.
 *
 * Read-only. The old admin had a disbursement create form and this deliberately
 * does not reinstate it: moving money out from a dashboard with no approval
 * step, no second pair of eyes, and a write tiering that is a confirmation
 * prompt rather than an authorization boundary is not something this surface
 * should be able to do.
 */
function DisbursementsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const filters = toFilters(search);
  const { data, error, isPending, isFetching, refetch } = useDisbursements(filters);

  function patch(next: Partial<DisbursementSearch>) {
    void navigate({
      search: (previous) => ({
        ...previous,
        ...next,
        offset: "offset" in next ? next.offset : 0,
      }),
      replace: true,
    });
  }

  function clearFilters() {
    void navigate({ search: (previous) => ({ limit: previous.limit }), replace: true });
  }

  const rows = data?.items ?? [];
  const filtered = hasActiveFilters(search);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Disbursements"
        description="Payouts recorded against Kugie's account. Read-only — these are not created here."
      />

      <FilterBar
        search={search}
        onChange={patch}
        onClear={clearFilters}
        hasFilters={filtered}
      />

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Created</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Destination</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {isPending ? (
              <TableSkeleton columns={COLUMNS} />
            ) : error ? (
              <TableError
                columns={COLUMNS}
                message={errorMessage(error)}
                onRetry={() => void refetch()}
              />
            ) : rows.length === 0 ? (
              <TableEmpty
                columns={COLUMNS}
                message={
                  filtered
                    ? "No disbursements match these filters."
                    : "No disbursements have been recorded yet."
                }
                onClear={filtered ? clearFilters : undefined}
              />
            ) : (
              rows.map((row) => (
                <TableRow key={row.disbursement_id}>
                  <TableCell>
                    <Link
                      to="/disbursements/$disbursementId"
                      params={{ disbursementId: row.disbursement_id }}
                      className="text-primary hover:underline"
                    >
                      {formatDateTime(row.created_at)}
                    </Link>
                  </TableCell>

                  <TableCell className="text-muted-foreground">
                    {orDash(row.disbursement_method)}
                  </TableCell>

                  <TableCell className="max-w-[18rem] truncate font-mono text-[0.8125rem]">
                    {orDash(row.destination)}
                  </TableCell>

                  {/* amount is genuinely NOT NULL here, unlike the settlement
                      money columns, so it is never an em dash. */}
                  <TableCell className="text-right tabular-nums">
                    {formatIDR(row.amount)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data ? (
          <PaginationBar
            pagination={data.pagination}
            onChange={(next) => patch(next)}
            disabled={isFetching}
          />
        ) : null}
      </div>
    </div>
  );
}

function FilterBar({
  search,
  onChange,
  onClear,
  hasFilters,
}: {
  search: DisbursementSearch;
  onChange: (patch: Partial<DisbursementSearch>) => void;
  onClear: () => void;
  hasFilters: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
      {/* Both free text rather than dropdowns: neither column is constrained,
          so any set offered here would be a guess that hides rows. */}
      <Field label="Method">
        <DebouncedInput
          value={search.method ?? ""}
          onCommit={(value) => onChange({ method: value || undefined })}
          placeholder="Starts with…"
          className="w-44"
        />
      </Field>

      <Field label="Destination">
        <DebouncedInput
          value={search.destination ?? ""}
          onCommit={(value) => onChange({ destination: value || undefined })}
          placeholder="Starts with…"
          className="w-56"
        />
      </Field>

      <Field label="From">
        <Input
          type="date"
          value={search.start_date ?? ""}
          onChange={(event) => onChange({ start_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      <Field label="To">
        <Input
          type="date"
          value={search.end_date ?? ""}
          onChange={(event) => onChange({ end_date: event.target.value || undefined })}
          className="w-[8.5rem]"
        />
      </Field>

      {hasFilters ? (
        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          <XIcon className="size-3.5" />
          Clear
        </Button>
      ) : null}
    </div>
  );
}
